import { Routes, Route } from 'react-router-dom';
import { Navigate } from '@usas/ui-web/wrappers/ReactRouterDom';

import GroupContextView from './GroupContextView';

import GroupSearch from './groupSearch/GroupSearch';
import OrgUnitSelection from './groupAdd/orgUnitSelection/OrgUnitSelection';
import GroupSelectType from './groupAdd/selectGroupType/GroupSelectType';
import GroupListAdd from './groupAdd/addList/GroupListAdd';
import GroupRegistrationAdd from './groupAdd/addRegistration/GroupRegistrationAdd';
import GroupCompetitorListAdd from './groupAdd/addCompetitorList/GroupCompetitorListAdd';
import GroupCampAdd from './groupAdd/addCamp/GroupCampAdd';
import GroupSAAAdd from './groupAdd/addSAA/GroupSAAAdd';
import GroupInfo from './groupInfo/GroupInfo';
import GroupAttributes from './groupAttributes/GroupAttributes';
import GroupRegistration from './groupRegistration/GroupRegistration';
import GroupMembers from './groupMembers/GroupMembers';
import GroupMemberAdd from './groupMembers/addMember/GroupMemberAdd';
import GroupMemberImport from './groupMemberImport/GroupMemberImport';
import GroupInviteAlternateMembers from './groupMembers/inviteAlternateMembers/GroupInviteAlternateMembers';
import GroupInviteAlternateMembersIMX from './groupMembers/inviteAlternateMembersIMX/GroupInviteAlternateMembersIMX';
import GroupSelection from './groupSelection/GroupSelection';
import GroupMyGroups from './groupMyGroups/GroupMyGroups';
import GroupMemberEdit from './groupMembers/editMember/GroupMemberEdit';
import GroupMembersAdd from './groupAdd/addMembers/GroupMembersAdd';
import GroupReporting from './groupReporting/GroupReporting';

import ErrorBoundary from '@usas/ui-web/components/errors/ErrorBoundary';
import LoadingModal from '@usas/ui-web/components/dialogs/LoadingModal';
import SecuredRouteElement from '@usas/ui-web/components/security/SecuredRouteElement';
import ErrorBoundaryFallback from '@usas/ui-web/components/errors/ErrorBoundaryFallback';

import useNavRoutes from '@usas/ui-web/state/security/UseNavRoutes';

import LscContextProvider from '@usas/ui-web/state/lsc/LscContextProvider';
import GroupAddContextProvider from '../../state/groupAdd/GroupAddContextProvider';
import OrgUnitContextProvider from '@usas/ui-web/state/orgUnit/OrgUnitContextProvider';
import GroupFiltersContextProvider from '@usas/ui-web/state/groupSearch/GroupFiltersContextProvider';
import OrgGroupTypesContextProvider from '@usas/ui-web/state/orgGroupTypes/OrgGroupTypesContextProvider';
import GroupCategoryContextProvider from '@usas/ui-web/state/groupCategory/GroupCategoryContextProvider';
import SelectOrgUnitContextProvider from '@usas/ui-web/state/selectOrgUnit/SelectOrgUnitContextProvider';
import PersonOrgRoleContextProvider from '@usas/ui-web/state/personOrgRole/PersonOrgRoleContextProvider';
import OrgRoleHierarchyContextProvider from '@usas/ui-web/state/orgRoleHierarchy/OrgRoleHierarchyContextProvider';
import OrgGroupCategoriesContextProvider from '@usas/ui-web/state/orgGroupCategories/OrgGroupCategoriesContextProvider';
import OrgGroupPropertiesContextProvider from '@usas/ui-web/state/orgGroupProperties/OrgGroupPropertiesContextProvider';
import OrgRoleStaffRoleHierarchyContextProvider from '@usas/ui-web/state/orgRoleStaffRoleHierarchy/OrgRoleStaffRoleHierarchyContextProvider';

import GroupContextProvider from '@usas/ui-web/state/group/GroupContextProvider';
import GroupCoachesContextProvider from '@usas/ui-web/state/groupCoaches/GroupCoachesContextProvider';
import GroupFacilitiesContextProvider from '@usas/ui-web/state/groupFacilities/GroupFacilitiesContextProvider';
import GroupPersonCoachesContextProvider from '@usas/ui-web/state/groupPersonCoaches/GroupPersonCoachesContextProvider';
import GroupMembersContextProvider from '@usas/ui-web/state/group/GroupMembersContextProvider';
import SAAGroupFiltersContextProvider from '../../state/scholasticAllAmericaGroupFilters/SAAGroupFiltersContextProvider';

const GroupsContextRoutesContextProviders = ({ children }) => (
  <GroupContextProvider>
    <GroupMembersContextProvider>
      <GroupFacilitiesContextProvider>
        <GroupCoachesContextProvider>
          <GroupPersonCoachesContextProvider>
            <SAAGroupFiltersContextProvider>
              {children}
            </SAAGroupFiltersContextProvider>
          </GroupPersonCoachesContextProvider>
        </GroupCoachesContextProvider>
      </GroupFacilitiesContextProvider>
    </GroupMembersContextProvider>
  </GroupContextProvider>
);

const GroupsBasicRoutesContextProviders = ({ children }) => (
  <OrgUnitContextProvider>
    <LscContextProvider>
      <OrgRoleHierarchyContextProvider>
        <OrgRoleStaffRoleHierarchyContextProvider>
          <PersonOrgRoleContextProvider>
            <SelectOrgUnitContextProvider>
              <OrgGroupTypesContextProvider>
                <OrgGroupCategoriesContextProvider>
                  <OrgGroupPropertiesContextProvider>
                    <GroupCategoryContextProvider>
                      <GroupFiltersContextProvider>
                        <GroupAddContextProvider>
                          {children}
                        </GroupAddContextProvider>
                      </GroupFiltersContextProvider>
                    </GroupCategoryContextProvider>
                  </OrgGroupPropertiesContextProvider>
                </OrgGroupCategoriesContextProvider>
              </OrgGroupTypesContextProvider>
            </SelectOrgUnitContextProvider>
          </PersonOrgRoleContextProvider>
        </OrgRoleStaffRoleHierarchyContextProvider>
      </OrgRoleHierarchyContextProvider>
    </LscContextProvider>
  </OrgUnitContextProvider>
);

const ContextRoutes = ({ navRoutes }) => (
  <GroupsContextRoutesContextProviders>
    <GroupContextView>
      <Routes>
        <Route path={navRoutes['GROUP_INFO']?.path}
          element={
            <SecuredRouteElement
              navRoute={navRoutes['GROUP_INFO']}
              editElement={<GroupInfo />} />
          } />
        <Route path={navRoutes['GROUP_ATTRIBUTES']?.path}
          element={
            <SecuredRouteElement
              navRoute={navRoutes['GROUP_ATTRIBUTES']}
              editElement={<GroupAttributes />} />
          } />
        <Route path={navRoutes['GROUP_REGISTRATION']?.path}
          element={
            <SecuredRouteElement
              navRoute={navRoutes['GROUP_REGISTRATION']}
              editElement={<GroupRegistration />} />
          } />
        <Route path={navRoutes['GROUP_MEMBERS']?.path}
          element={
            <SecuredRouteElement
              navRoute={navRoutes['GROUP_MEMBERS']}
              editElement={<GroupMembers />} />
          } />
        <Route path={navRoutes['GROUP_MEMBERS_ADD']?.path}
          element={
            <SecuredRouteElement
              navRoute={navRoutes['GROUP_MEMBERS_ADD']}
              editElement={<GroupMemberAdd />} />
          } />
        <Route path={navRoutes['GROUP_MEMBERS_EDIT']?.path}
          element={
            <SecuredRouteElement
              navRoute={navRoutes['GROUP_MEMBERS_EDIT']}
              editElement={<GroupMemberEdit />} />
          } />
        <Route path={navRoutes['GROUP_MEMBERS_INVITE_ALTERNATE']?.path}
          element={
            <SecuredRouteElement
              navRoute={navRoutes['GROUP_MEMBERS_INVITE_ALTERNATE']}
              editElement={<GroupInviteAlternateMembers />} />
          } />
        <Route path={navRoutes['GROUP_MEMBERS_INVITE_ALTERNATE_IMX']?.path}
          element={
            <SecuredRouteElement
              navRoute={navRoutes['GROUP_MEMBERS_INVITE_ALTERNATE_IMX']}
              editElement={<GroupInviteAlternateMembersIMX />} />
          } />
        <Route path={navRoutes['GROUP_MEMBER_IMPORT']?.path}
          element={
            <SecuredRouteElement
              navRoute={navRoutes['GROUP_MEMBER_IMPORT']}
              editElement={<GroupMemberImport />} />
          } />
        <Route path={navRoutes['GROUP_REPORTING']?.path}
          element={
            <SecuredRouteElement
              navRoute={navRoutes['GROUP_REPORTING']}
              editElement={<GroupReporting />} />
          } />
      </Routes>
    </GroupContextView>
  </GroupsContextRoutesContextProviders>
);

const GroupsBasicRoutes = ({ navRoutes }) => (
  <GroupsBasicRoutesContextProviders>
    <Routes>
      <Route path={navRoutes['GROUP_SEARCH']?.path}
        element={
          <SecuredRouteElement
            navRoute={navRoutes['GROUP_SEARCH']}
            editElement={<GroupSearch />} />
        } />
      <Route path={navRoutes['GROUP_SELECTION']?.path}
        element={
          <SecuredRouteElement
            navRoute={navRoutes['GROUP_SELECTION']}
            editElement={<GroupSelection />} />
        } />
      <Route path={navRoutes['GROUP_MY_GROUPS']?.path}
        element={
          <SecuredRouteElement
            navRoute={navRoutes['GROUP_MY_GROUPS']}
            editElement={<GroupMyGroups />} />
        } />
      <Route path={navRoutes['GROUP_ADD']?.path}
        element={
          <SecuredRouteElement
            navRoute={navRoutes['GROUP_ADD']}
            editElement={<OrgUnitSelection />} />
        } />
      <Route path={navRoutes['GROUP_ADD_SELECTION']?.path}
        element={
          <SecuredRouteElement
            navRoute={navRoutes['GROUP_ADD_SELECTION']}
            editElement={<GroupSelectType />} />
        } />
      <Route path={navRoutes['GROUP_ADD_LIST']?.path}
        element={
          <SecuredRouteElement
            navRoute={navRoutes['GROUP_ADD_LIST']}
            editElement={<GroupListAdd />} />
        } />
      <Route path={navRoutes['GROUP_ADD_COMPETITOR_LIST']?.path}
        element={
          <SecuredRouteElement
            navRoute={navRoutes['GROUP_ADD_COMPETITOR_LIST']}
            editElement={<GroupCompetitorListAdd />} />
        } />
      <Route path={navRoutes['GROUP_ADD_CAMP']?.path}
        element={
          <SecuredRouteElement
            navRoute={navRoutes['GROUP_ADD_CAMP']}
            editElement={<GroupCampAdd />} />
        } />
      <Route path={navRoutes['GROUP_ADD_SCHOLASTIC']?.path}
        element={
          <SecuredRouteElement
            navRoute={navRoutes['GROUP_ADD_SCHOLASTIC']}
            editElement={<GroupSAAAdd />} />
        } />
      <Route path={navRoutes['GROUP_ADD_REGISTRATION']?.path}
        element={
          <SecuredRouteElement
            navRoute={navRoutes['GROUP_ADD_REGISTRATION']}
            editElement={<GroupRegistrationAdd />} />
        } />
      <Route path={navRoutes['GROUP_ADD_MEMBERS']?.path}
        element={
          <SecuredRouteElement
            navRoute={navRoutes['GROUP_ADD_MEMBERS']}
            editElement={<GroupMembersAdd />} />
        } />
      <Route path={'/*'} element={<ContextRoutes navRoutes={navRoutes} />} />
      <Route path={'/'} element={<Navigate to={navRoutes['GROUP_SEARCH']?.route} replace />} />
    </Routes>
  </GroupsBasicRoutesContextProviders>
);

const Group = () => {
  const { navRoutes, isReadyToRoute } = useNavRoutes();

  if (isReadyToRoute !== true) {
    return <LoadingModal />;
  }

  return (
    <ErrorBoundary fallback={<ErrorBoundaryFallback />}>
      <GroupsBasicRoutes navRoutes={navRoutes} />
    </ErrorBoundary>
  );
};
export default Group;